import * as R from 'ramda';
import { SearchAlert } from 'ha/services/apiV2/types/SearchAlert';
import { formatDate, NO_START, NO_END } from 'ha/helpers/hadate';

const CURRENCY_KEYS = ['minPrice', 'maxPrice', 'currency'];
const RELEVANT_KEYS = ['startDate', 'endDate', 'kinds'].concat(CURRENCY_KEYS);
const EMPTY_VALUES = [0, NO_END, NO_START];

export const pickRelevantKeys = (alert: SearchAlert): SearchAlert =>
  R.pickBy(
    val => R.not(R.isNil(val) || R.contains(val, EMPTY_VALUES)),
    R.pick(RELEVANT_KEYS, alert),
  );

export const areDatesEqual = (d1?: string, d2?: string): boolean => {
  if (!d1 && !d2) {
    return true;
  }

  if (!d1 || !d2) {
    return false;
  }

  return (
    formatDate('en', 'YYYY-MM-DD', d1) === formatDate('en', 'YYYY-MM-DD', d2)
  );
};

export const areKindsEqual = (
  kinds1?: string[],
  kinds2?: string[],
): boolean => {
  if (!kinds1 && !kinds2) {
    return true;
  }

  if (!kinds1 || !kinds2) {
    return false;
  }

  return R.isEmpty(R.symmetricDifference(kinds1, kinds2));
};

export default (a1?: SearchAlert, a2?: SearchAlert): boolean => {
  if (!a1 && !a2) {
    return true;
  }

  if (!a1 || !a2) {
    return false;
  }

  // Pick values that are defined for each alert.
  const alert1 = pickRelevantKeys(a1);
  const alert2 = pickRelevantKeys(a2);

  // If one alert has more restrictions than the other, they are different (optimization)
  if (!R.equals(Object.keys(alert1), Object.keys(alert2))) {
    return false;
  }

  const datesAreEqual =
    areDatesEqual(alert1.startDate, alert2.startDate) &&
    areDatesEqual(alert1.endDate, alert2.endDate);

  const kindsAreEqual = areKindsEqual(alert1.kinds, alert2.kinds);

  const pricesAreEqual = CURRENCY_KEYS.every(
    key => alert1[key] === alert2[key],
  );

  return [datesAreEqual, kindsAreEqual, pricesAreEqual].every(Boolean);
};
